import {
  ACTIVATION_COLORS,
  ACTIVATION_LABELS,
  ACTIVATION_ORDER,
  REASON_PRIORITY,
  colorForSupport,
  resolveSupportLabel,
} from '../classification';
import type { DrillLocation, ExplorationState, ViewMode } from '../../types/exploration';
import { isInstanceLocation, structuralPathStrings } from '../drill/drillLocation';

export interface SidebarPalette {
  field: 'activationStatus' | 'supportStatus';
  order: string[];
  label: (key: string) => string;
  color: (key: string) => string;
}

export function currentInnerPalette(view: ViewMode): SidebarPalette {
  return sidebarBreakdownPalette(view === 'activation' ? 'activation' : 'support');
}

export const autoscalingPotentialPerGroupHeading = 'Autoscaling Potential per Group';
export const activationByGroupHeading = 'Activation by Group';
export const attachedVolumesHeading = 'Volumes';

export type SidebarGroupBarsMode = 'exploration' | 'attachedVolumes';

export function groupBarHeader(
  state: Pick<ExplorationState, 'view' | 'drillPath'>
): string {
  if (state.view === 'activation') {
    return activationByGroupHeading;
  }
  if (state.view === 'exploration') {
    return autoscalingPotentialPerGroupHeading;
  }
  const tail =
    state.drillPath.reasonLabel ??
    state.drillPath.statusLabel ??
    structuralPathStrings(state.drillPath, { groupByAccount: false }).at(-1);
  return tail ? `Inside ${tail}` : 'Installation groups';
}

export function resolveGroupBarsHeading(
  state: Pick<ExplorationState, 'view' | 'drillPath'>,
  mode: SidebarGroupBarsMode,
  headingOverride?: string
): string {
  if (headingOverride) return headingOverride;
  if (mode === 'attachedVolumes') return attachedVolumesHeading;
  return groupBarHeader(state);
}

/**
 * True when the drill location has reached an instance (volume-leaf canvas scope).
 * Independent of grouping mode: Group:None can still focus an instance (borrowed
 * cluster-shaped drill), and volume tooltips / attached-volumes sidebar need that.
 * False while a focused-activation category (Unsupported / Pending setup) is set
 * without a reason chosen yet — the canvas still shows reason bubbles there, not
 * volume leaves, so the sidebar must keep grouping by reason too.
 */
export function isInstanceDrillLevel(location: DrillLocation): boolean {
  if (location.focusedActivationKey && !location.reasonLabel) return false;
  return isInstanceLocation(location);
}

/**
 * True when the canvas is scoped to a status/reason leaf (sidebar shows attached
 * volumes). Instance volume-leaf scope is owned by `isInstanceDrillLevel`.
 * Focused Unsupported has no reason step — it goes straight to volumes, so it's
 * true as soon as it's focused. Focused Pending-setup without a reason is still
 * false — canvas shows reason bubbles there, not volume leaves.
 */
export function isStatusDrillLevel(location: DrillLocation): boolean {
  if (location.reasonLabel) return true;
  if (location.statusLabel) return true;
  if (location.focusedActivationKey === 'unsupported') return true;
  return false;
}

/**
 * True when sidebar scope is a single status/reason category showing volume leaves
 * (e.g. Activation Unsupported, Activation Pending setup → No Autoscaler, or
 * Exploration region → Hypervisor). Hides the redundant signal metric
 * and breakdown card.
 */
export function isCategoryLeafSidebarScope(location: DrillLocation): boolean {
  return (
    location.statusLabel != null ||
    location.reasonLabel != null ||
    location.focusedActivationKey === 'unsupported'
  );
}

export type SidebarBreakdownVariant = 'activation' | 'support';

const activationSidebarPalette: SidebarPalette = {
  field: 'activationStatus',
  order: ACTIVATION_ORDER,
  label: (k) => ACTIVATION_LABELS[k as keyof typeof ACTIVATION_LABELS] || k,
  color: (k) => ACTIVATION_COLORS[k as keyof typeof ACTIVATION_COLORS],
};

const supportSidebarPalette: SidebarPalette = {
  field: 'supportStatus',
  order: ['supported', ...REASON_PRIORITY],
  label: resolveSupportLabel,
  color: (k) => colorForSupport(k),
};

export function sidebarBreakdownPalette(variant: SidebarBreakdownVariant): SidebarPalette {
  return variant === 'activation' ? activationSidebarPalette : supportSidebarPalette;
}
